import React, { ReactNode, useState, useEffect, useRef, useCallback } from "react";
import { Dropdown } from "..";
import { t } from "i18next";

export interface location {
  electricityCategoryProviderId: string;
  electricityProductId: string;
  gasCategoryProviderId: string;
  gasProductId: string;
  id: number;
  mainName: string;
  name: string;
  partName: string;
}

export interface item {
  text: string;
  value: string;
}

interface props {
  value: string;
  options: item[];
  onChange: (val: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  error?: string;
  className?: string;
  required?: boolean;
  title?: string | ReactNode;
  placeholder?: string;
  onValidate?: (err: string) => void;
  runValidation?: boolean;
  loading?: boolean;
  customValidation?: ((value: unknown) => string | undefined) | Array<(value: unknown) => string | undefined>;
}

const Location = ({
  value,
  onChange,
  onValidate,
  onBlur: onBlurProp,
  onFocus,
  error,
  className,
  options,
  required,
  title,
  placeholder,
  runValidation,
  loading,
  customValidation,
}: props) => {
  const [internalError, setInternalError] = useState(error || "");
  const errorRef = useRef<string>(error || "");

  useEffect(() => {
    if (errorRef.current !== internalError) {
      errorRef.current = internalError;
      onValidate && onValidate(internalError);
    }
  }, [internalError, onValidate]);

  useEffect(() => {
    if (errorRef.current !== error && error !== undefined) {
      setInternalError(error);
    }
  }, [error]);

  const validate = useCallback(() => {
    if (required && !value) {
      setInternalError(t("validations.required") || "");
    }
  }, [required, value]);

  useEffect(() => {
    if (runValidation) {
      validate();
    }
  }, [runValidation, validate]);

  const handleChange = (val: string) => {
    if (!!internalError) {
      setInternalError("");
    }
    onChange(val);
  };

  const onBlur = () => {
    validate();
    onBlurProp && onBlurProp();
  };

  return (
    <Dropdown
      name="location"
      value={value}
      onChange={handleChange}
      options={options}
      className={className}
      errorMessage={internalError}
      error={!!internalError}
      showValidation={!!internalError}
      disabled={options.length < 1}
      onBlur={onBlur}
      onFocus={onFocus}
      title={title}
      placeholder={placeholder || t("sof.location.placeholder") || ""}
      loading={loading}
      validate={customValidation}
      required={required}
    />
  );
};

export default Location;
